import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../game/constants';
import { DialogManager, DialogConfig } from '../lib/DialogManager';

const SCALE = 2;

/**
 * LimoArrivalScene - Intro cutscene where Kim arrives at the Supreme Court
 * 
 * Sequence:
 * 1. Limo pulls up to the Temple of Justice
 * 2. DJT exits first, then JD, then Kim
 * 3. DJT speaks about Clarence Thomas and DC
 * 4. JD gets an urgent call, both DJT and JD leave
 * 5. Kim can walk around the outdoor area with grass
 * 6. Kim enters the Temple (transition to WorldScene)
 */
export class LimoArrivalScene extends Phaser.Scene {
  private dialogManager!: DialogManager;
  private limo!: Phaser.GameObjects.Sprite;
  private djt!: Phaser.GameObjects.Sprite;
  private jd!: Phaser.GameObjects.Sprite;
  private kim!: Phaser.GameObjects.Sprite;
  private templeBackground!: Phaser.GameObjects.Sprite;
  private skipText!: Phaser.GameObjects.Text;
  private enterPrompt!: Phaser.GameObjects.Text;
  
  // Crowd sprites for celebration
  private crowdLeft: Phaser.GameObjects.Sprite[] = [];
  private crowdRight: Phaser.GameObjects.Sprite[] = [];
  
  // Layers for depth sorting
  private backgroundLayer!: Phaser.GameObjects.Container;
  private grassLayer!: Phaser.GameObjects.Container;
  private crowdLayer!: Phaser.GameObjects.Container;
  private entityLayer!: Phaser.GameObjects.Container;
  private uiLayer!: Phaser.GameObjects.Container;
  
  // Walking mode after cutscene
  private walkingEnabled = false;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private templeEntrance = { x: GAME_WIDTH / 2, y: 80 };

  constructor() {
    super({ key: 'LimoArrivalScene' });
  }

  create(): void {
    console.info('[LimoArrivalScene] create() started');
    
    // Create layer containers for depth sorting
    this.backgroundLayer = this.add.container(0, 0);
    this.grassLayer = this.add.container(0, 0);
    this.crowdLayer = this.add.container(0, 0);
    this.entityLayer = this.add.container(0, 0);
    this.uiLayer = this.add.container(0, 0);
    
    // Create background (Supreme Court exterior with grass)
    this.createBackground();
    
    // Create grass tiles for the outdoor area
    this.createGrassArea();
    
    // Create the crowd (cheering supporters and media)
    this.createCrowd();
    
    // Create the limo (starts off-screen right)
    this.createLimo();
    
    // Create characters (hidden initially)
    this.createCharacters();
    
    // Create dialog manager
    this.dialogManager = new DialogManager(this);
    
    // Add skip instruction
    this.skipText = this.add.text(GAME_WIDTH - 20, 20, 'Press ESC to skip', {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: '8px',
      color: '#888888',
    }).setOrigin(1, 0);
    this.uiLayer.add(this.skipText);
    
    // Add temple entrance prompt (hidden initially)
    this.enterPrompt = this.add.text(GAME_WIDTH / 2, 110, 'Press SPACE to enter', {
      fontFamily: '"Press Start 2P", monospace',
      fontSize: '10px',
      color: '#ffff00',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5).setVisible(false);
    this.uiLayer.add(this.enterPrompt);
    
    // Setup input
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.ESC).on('down', () => {
      this.skipToHub();
    });
    this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE).on('down', () => {
      if (this.walkingEnabled && this.isNearTemple()) {
        this.enterTemple();
      }
    });
    
    // Start the arrival sequence
    this.time.delayedCall(500, () => {
      this.startArrivalSequence();
    });
  }
  
  update(): void {
    if (!this.walkingEnabled || !this.kim) return;
    
    const speed = 100;
    let vx = 0;
    let vy = 0;
    
    // Handle movement input
    if (this.cursors.left.isDown) {
      vx = -speed;
      if (this.anims.exists('kim-walk-left')) {
        this.kim.play('kim-walk-left', true);
      }
    } else if (this.cursors.right.isDown) {
      vx = speed;
      if (this.anims.exists('kim-walk-right')) {
        this.kim.play('kim-walk-right', true);
      }
    }
    
    if (this.cursors.up.isDown) {
      vy = -speed;
      if (vx === 0 && this.anims.exists('kim-walk-up')) {
        this.kim.play('kim-walk-up', true);
      }
    } else if (this.cursors.down.isDown) {
      vy = speed;
      if (vx === 0 && this.anims.exists('kim-walk-down')) {
        this.kim.play('kim-walk-down', true);
      }
    }
    
    // Apply movement with bounds checking
    if (vx !== 0 || vy !== 0) {
      const delta = this.game.loop.delta / 1000;
      const newX = Phaser.Math.Clamp(this.kim.x + vx * delta, 40, GAME_WIDTH - 40);
      const newY = Phaser.Math.Clamp(this.kim.y + vy * delta, 100, GAME_HEIGHT - 40);
      this.kim.setPosition(newX, newY);
    } else {
      // Idle when not moving
      if (this.anims.exists('kim-idle')) {
        this.kim.play('kim-idle', true);
      }
    }
    
    // Check proximity to temple entrance
    this.enterPrompt.setVisible(this.isNearTemple());
  }
  
  private isNearTemple(): boolean {
    const dist = Phaser.Math.Distance.Between(
      this.kim.x, this.kim.y,
      this.templeEntrance.x, this.templeEntrance.y
    );
    return dist < 60;
  }
  
  private enterTemple(): void {
    this.walkingEnabled = false;
    this.enterPrompt.setVisible(false);
    
    // Walk Kim up to temple
    if (this.anims.exists('kim-walk-up')) {
      this.kim.play('kim-walk-up');
    }
    
    this.tweens.add({
      targets: this.kim,
      y: this.templeEntrance.y,
      duration: 800,
      ease: 'Quad.easeIn',
      onComplete: () => {
        // Fade and transition
        this.cameras.main.fadeOut(800, 0, 0, 0);
        this.time.delayedCall(800, () => {
          this.skipToHub();
        });
      }
    });
  }

  private createBackground(): void {
    // Sky background - use the noon sky image if available
    if (this.textures.exists('sky-noon')) {
      const skyBg = this.add.image(GAME_WIDTH / 2, 0, 'sky-noon');
      skyBg.setOrigin(0.5, 0);
      // Scale to fit width while maintaining aspect ratio
      const scaleX = GAME_WIDTH / skyBg.width;
      skyBg.setScale(scaleX);
      this.backgroundLayer.add(skyBg);
    } else {
      // Fallback: Sky gradient background
      const sky = this.add.rectangle(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 4,
        GAME_WIDTH,
        GAME_HEIGHT / 2,
        0x87ceeb // Light sky blue
      );
      this.backgroundLayer.add(sky);
    }
    
    // Green grass base for outdoor area (below building area only)
    const grassBase = this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 100,  // Positioned lower so building is on top
      GAME_WIDTH,
      GAME_HEIGHT / 2 - 20,   // Smaller height to not overlap building
      0x3a5a3a // Darker forest green base
    );
    this.backgroundLayer.add(grassBase);
    
    // Try to use supreme_court_backdrop image as backdrop - scale to fit width
    if (this.textures.exists('supreme_court_backdrop')) {
      this.templeBackground = this.add.sprite(
        GAME_WIDTH / 2,
        0,
        'supreme_court_backdrop'
      );
      // The image is 2824x1264 - scale to fit the game width
      const scaleToFit = GAME_WIDTH / 2824;
      this.templeBackground.setScale(scaleToFit * 1.1); // Slightly larger for presence
      this.templeBackground.setOrigin(0.5, 0);
      this.templeBackground.setY(0);
      this.backgroundLayer.add(this.templeBackground);
    } else {
      // Fallback: Create a more detailed Supreme Court representation
      // Main building facade
      const buildingWidth = 400;
      const buildingHeight = 140;
      const buildingY = 70;
      
      // Stone building base
      const building = this.add.rectangle(
        GAME_WIDTH / 2,
        buildingY,
        buildingWidth,
        buildingHeight,
        0xd4d4c8 // Light stone gray
      );
      building.setStrokeStyle(2, 0x888888);
      this.backgroundLayer.add(building);
      
      // Roof/pediment (triangular top)
      const roof = this.add.triangle(
        GAME_WIDTH / 2, buildingY - buildingHeight / 2 - 20,
        -buildingWidth / 2 + 20, 20,
        0, -20,
        buildingWidth / 2 - 20, 20,
        0xc0c0b0
      );
      roof.setStrokeStyle(2, 0x888888);
      this.backgroundLayer.add(roof);
      
      // Columns (8 columns across the facade)
      const columnCount = 8;
      const columnWidth = 18;
      const columnSpacing = (buildingWidth - 60) / (columnCount - 1);
      const columnStartX = GAME_WIDTH / 2 - buildingWidth / 2 + 30;
      
      for (let i = 0; i < columnCount; i++) {
        const colX = columnStartX + i * columnSpacing;
        const column = this.add.rectangle(
          colX,
          buildingY + 20,
          columnWidth,
          buildingHeight - 20,
          0xe8e8dc
        );
        column.setStrokeStyle(1, 0x999999);
        this.backgroundLayer.add(column);
      }
      
      // Dark marble sign with "EQUAL JUSTICE UNDER LAW"
      const signWidth = 280;
      const signHeight = 18;
      const signY = buildingY - buildingHeight / 2 + 20;
      
      const signBg = this.add.rectangle(
        GAME_WIDTH / 2,
        signY,
        signWidth,
        signHeight,
        0x2a2a3a // Dark marble color
      );
      this.backgroundLayer.add(signBg);
      
      const signText = this.add.text(
        GAME_WIDTH / 2,
        signY,
        'EQUAL JUSTICE UNDER LAW',
        {
          fontFamily: '"Press Start 2P", monospace',
          fontSize: '7px',
          color: '#f0f0e8',
        }
      ).setOrigin(0.5);
      this.backgroundLayer.add(signText);
      
      // Stone steps below the building
      for (let i = 0; i < 5; i++) {
        const stepWidth = buildingWidth - 20 + i * 10;
        const stepY = buildingY + buildingHeight / 2 + 5 + i * 8;
        const step = this.add.rectangle(
          GAME_WIDTH / 2,
          stepY,
          stepWidth,
          8,
          0xc8c8bc
        );
        step.setStrokeStyle(1, 0x888888);
        this.backgroundLayer.add(step);
      }
    }
    
    // Add some trees from the trees atlas on both sides
    if (this.textures.exists('trees')) {
      const treePositions = [
        { x: 40, y: 160 },
        { x: GAME_WIDTH - 40, y: 160 },
        { x: 70, y: 220 },
        { x: GAME_WIDTH - 70, y: 220 },
        { x: 30, y: 280 },
        { x: GAME_WIDTH - 30, y: 280 },
      ];
      
      treePositions.forEach((pos, i) => {
        const frameNum = (i % 5) + 1;
        const frameName = `trees_${String(frameNum).padStart(4, '0')}`;
        const tree = this.add.sprite(pos.x, pos.y, 'trees', frameName);
        tree.setScale(0.12);
        this.backgroundLayer.add(tree);
      });
    }
    
    // Road at the bottom
    const road = this.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT - 40,
      GAME_WIDTH,
      60,
      0x333344 // Dark asphalt
    );
    this.backgroundLayer.add(road);
    
    // Road lines
    for (let x = 0; x < GAME_WIDTH; x += 60) {
      const line = this.add.rectangle(x + 30, GAME_HEIGHT - 40, 40, 4, 0xffff88);
      this.backgroundLayer.add(line);
    }
  }
  
  private createGrassArea(): void {
    // Add grass texture patches if tileset-grass is loaded
    // Use prettier grass tiles if available
    const grassTileKeys = ['grass-a', 'grass-b', 'grass-c', 'grass-d', 'grass-e', 'grass-f'];
    const availableGrassTiles = grassTileKeys.filter(key => this.textures.exists(key));
    
    if (availableGrassTiles.length > 0) {
      // Create a more natural grass pattern with varied tiles
      const tileSize = 16; // These tiles are 16x16
      // Start grass below the building steps (building ends ~Y=177)
      const grassStartY = 185;
      
      for (let y = grassStartY; y < GAME_HEIGHT - 70; y += tileSize) {
        for (let x = 0; x < GAME_WIDTH; x += tileSize) {
          // Fill entire grass area (no path skip)
          const tileKey = availableGrassTiles[Math.floor(Math.random() * availableGrassTiles.length)];
          const grassSprite = this.add.image(x, y, tileKey);
          grassSprite.setOrigin(0, 0);
          grassSprite.setScale(1.2); // Slight scale up for better coverage
          this.grassLayer.add(grassSprite);
        }
      }
      
      // Add grass detail sprites (small tufts) for extra polish
      const grassDetailKeys = ['grass-detail-a', 'grass-detail-b', 'grass-detail-c', 'grass-detail-d', 'grass-detail-e'];
      const availableDetails = grassDetailKeys.filter(key => this.textures.exists(key));
      
      if (availableDetails.length > 0) {
        // Add decorative grass tufts randomly across the lawn (below building)
        for (let i = 0; i < 30; i++) {
          const x = Math.random() * GAME_WIDTH;
          const y = 185 + Math.random() * (GAME_HEIGHT - 270);  // Below building
          
          const detailKey = availableDetails[Math.floor(Math.random() * availableDetails.length)];
          const detail = this.add.image(x, y, detailKey);
          detail.setScale(1.5);
          detail.setAlpha(0.9);
          this.grassLayer.add(detail);
        }
      }
    } else if (this.textures.exists('tileset-grass')) {
      // Fallback to old tileset-grass approach
      const grassTile = this.textures.get('tileset-grass');
      const grassWidth = grassTile.source[0].width;
      const grassHeight = grassTile.source[0].height;
      
      // Tile grass across the middle area (below building)
      for (let y = 185; y < GAME_HEIGHT - 70; y += 32) {
        for (let x = 0; x < GAME_WIDTH; x += 32) {
          // Skip the center path area
          if (x > GAME_WIDTH / 2 - 50 && x < GAME_WIDTH / 2 + 50) continue;
          
          const grassSprite = this.add.image(x, y, 'tileset-grass');
          grassSprite.setOrigin(0, 0);
          grassSprite.setScale(32 / grassWidth, 32 / grassHeight);
          grassSprite.setAlpha(0.3); // Subtle overlay
          this.grassLayer.add(grassSprite);
        }
      }
    }
    
    // Add stone path tiles using the path assets if available
    if (this.textures.exists('grass-path-a') && this.textures.exists('grass-path-b')) {
      const pathCenterX = GAME_WIDTH / 2;
      // Path starts from building steps (Y=180) to bottom (before road)
      for (let y = 180; y < GAME_HEIGHT - 70; y += 16) {
        // Alternate path tiles for variety
        const tileKey = y % 32 === 0 ? 'grass-path-a' : 'grass-path-b';
        const pathTile = this.add.image(pathCenterX - 16, y, tileKey);
        pathTile.setOrigin(0, 0);
        this.grassLayer.add(pathTile);
        
        const pathTile2 = this.add.image(pathCenterX, y, tileKey);
        pathTile2.setOrigin(0, 0);
        this.grassLayer.add(pathTile2);
        
        const pathTile3 = this.add.image(pathCenterX + 16, y, tileKey);
        pathTile3.setOrigin(0, 0);
        this.grassLayer.add(pathTile3);
      }
    }
  }
  
  private createCrowd(): void {
    // Add crowd/media sprites on both sides of the path
    // Using crowd_media_parade atlas (288x232 per frame, frames 0001-0019)
    if (!this.textures.exists('crowd_media_parade')) {
      console.warn('[LimoArrivalScene] crowd_media_parade atlas not loaded');
      return;
    }
    
    // Left side crowd - positioned in the grass area
    const leftPositions = [
      { x: 50, y: 200, frame: '0001' },
      { x: 90, y: 240, frame: '0003' },
      { x: 40, y: 290, frame: '0005' },
      { x: 80, y: 180, frame: '0007' },
    ];
    
    // Right side crowd
    const rightPositions = [
      { x: GAME_WIDTH - 50, y: 200, frame: '0002' },
      { x: GAME_WIDTH - 90, y: 240, frame: '0004' },
      { x: GAME_WIDTH - 40, y: 290, frame: '0006' },
      { x: GAME_WIDTH - 80, y: 180, frame: '0008' },
    ];
    
    const crowdScale = 0.35; // Scale for visible crowd members (288x232 originals)
    
    leftPositions.forEach(pos => {
      const frameName = `crowd_media_parade_${pos.frame}`;
      if (this.textures.getFrame('crowd_media_parade', frameName)) {
        const crowdMember = this.add.sprite(pos.x, pos.y, 'crowd_media_parade', frameName);
        crowdMember.setScale(crowdScale);
        crowdMember.setAlpha(0); // Hidden initially
        this.crowdLayer.add(crowdMember);
        this.crowdLeft.push(crowdMember);
      }
    });
    
    rightPositions.forEach(pos => {
      const frameName = `crowd_media_parade_${pos.frame}`;
      if (this.textures.getFrame('crowd_media_parade', frameName)) {
        const crowdMember = this.add.sprite(pos.x, pos.y, 'crowd_media_parade', frameName);
        crowdMember.setScale(crowdScale);
        crowdMember.setAlpha(0); // Hidden initially  
        this.crowdLayer.add(crowdMember);
        this.crowdRight.push(crowdMember);
      }
    });
    
    console.info(`[LimoArrivalScene] Created ${this.crowdLeft.length + this.crowdRight.length} crowd members`);
  }
  
  private showCrowd(): void {
    // Fade in all crowd members with slight stagger
    const allCrowd = [...this.crowdLeft, ...this.crowdRight];
    
    allCrowd.forEach((crowdMember, i) => {
      this.tweens.add({
        targets: crowdMember,
        alpha: 1,
        duration: 300,
        delay: i * 50,
        ease: 'Quad.easeOut',
      });
    });
  }
  
  private cheerCrowd(): void {
    // Make the crowd cheer/wave by bobbing up and down
    const allCrowd = [...this.crowdLeft, ...this.crowdRight];
    
    allCrowd.forEach((crowdMember, i) => {
      const originalY = crowdMember.y;
      
      this.tweens.add({
        targets: crowdMember,
        y: originalY - 5,
        duration: 200,
        delay: i * 30,
        yoyo: true,
        repeat: 3,
        ease: 'Quad.easeInOut',
      });
      
      // Also do a slight scale pulse for excitement
      this.tweens.add({
        targets: crowdMember,
        scaleX: crowdMember.scaleX * 1.1,
        scaleY: crowdMember.scaleY * 1.1,
        duration: 150,
        delay: i * 30 + 100,
        yoyo: true,
        repeat: 2,
        ease: 'Quad.easeInOut',
      });
    });
  }

  private createLimo(): void {
    // Create limo sprite from the limo atlas
    if (this.textures.exists('limo')) {
      this.limo = this.add.sprite(
        GAME_WIDTH + 100, // Start off-screen right
        GAME_HEIGHT - 60,
        'limo',
        'limo_0001'
      );
      this.limo.setScale(0.4);
    } else {
      // Fallback: simple rectangle
      this.limo = this.add.sprite(GAME_WIDTH + 100, GAME_HEIGHT - 60, '');
      const limoRect = this.add.rectangle(0, 0, 120, 40, 0x111111);
      limoRect.setStrokeStyle(2, 0x333333);
      this.entityLayer.add(limoRect);
    }
    this.entityLayer.add(this.limo);
  }

  private createCharacters(): void {
    const exitX = GAME_WIDTH / 2 + 50;
    const exitY = GAME_HEIGHT - 90;
    
    // DJT - from djt_leader atlas (224x232 per frame)
    if (this.textures.exists('djt_leader')) {
      this.djt = this.add.sprite(exitX, exitY, 'djt_leader', 'djt_leader_0001');
      this.djt.setScale(0.35); // Scale up to ~80px tall for visibility
    } else {
      this.djt = this.add.sprite(exitX, exitY, 'monsters', 0);
      this.djt.setScale(SCALE);
    }
    this.djt.setVisible(false);
    this.entityLayer.add(this.djt);
    
    // JD - from jd_follower atlas (216x232 per frame)
    if (this.textures.exists('jd_follower')) {
      this.jd = this.add.sprite(exitX + 40, exitY, 'jd_follower', 'jd_follower_0001');
      this.jd.setScale(0.32); // Scale up to ~75px tall for visibility
    } else {
      this.jd = this.add.sprite(exitX + 40, exitY, 'monsters', 1);
      this.jd.setScale(SCALE * 0.8);
    }
    this.jd.setVisible(false);
    this.entityLayer.add(this.jd);
    
    // Kim - from k1_kim atlas (use noclothing version to match animations)
    if (this.textures.exists('k1_kim_with_noclothing_and_noweapons')) {
      this.kim = this.add.sprite(exitX + 80, exitY, 'k1_kim_with_noclothing_and_noweapons', 'k1_kim_with_noclothing_and_noweapons_0001');
      this.kim.setScale(0.15);
    } else {
      this.kim = this.add.sprite(exitX + 80, exitY, 'monsters', 2);
      this.kim.setScale(SCALE);
    }
    this.kim.setVisible(false);
    this.entityLayer.add(this.kim);
  }

  private startArrivalSequence(): void {
    // Show the crowd as the limo approaches
    this.showCrowd();
    
    // Play limo driving animation
    if (this.anims.exists('limo-drive')) {
      this.limo.play('limo-drive');
    }
    
    // Step 1: Limo drives in from right
    this.tweens.add({
      targets: this.limo,
      x: GAME_WIDTH / 2 + 80,
      duration: 2000,
      ease: 'Quad.easeOut',
      onComplete: () => {
        // Switch to idle animation when stopped
        if (this.anims.exists('limo-idle')) {
          this.limo.play('limo-idle');
        } else {
          this.limo.stop();
        }
        
        // Crowd cheers when limo stops
        this.cheerCrowd();
        
        // Step 2: Characters exit
        this.time.delayedCall(500, () => this.exitCharacters());
      }
    });
  }

  private exitCharacters(): void {
    // DJT exits first
    this.djt.setVisible(true);
    this.djt.setPosition(this.limo.x, this.limo.y);
    
    // Play walk animation if available
    if (this.anims.exists('djt-walk')) {
      this.djt.play('djt-walk');
    }
    
    this.tweens.add({
      targets: this.djt,
      x: GAME_WIDTH / 2 - 40,
      y: GAME_HEIGHT - 100,
      duration: 800,
      ease: 'Quad.easeOut',
      onComplete: () => {
        // Switch to idle animation
        if (this.anims.exists('djt-idle')) {
          this.djt.play('djt-idle');
        }
        
        // JD exits second and follows close behind DJT
        this.jd.setVisible(true);
        this.jd.setPosition(this.limo.x, this.limo.y);
        
        if (this.anims.exists('jd-walk')) {
          this.jd.play('jd-walk');
        }
        
        // JD positions slightly behind and to the right of DJT
        this.tweens.add({
          targets: this.jd,
          x: this.djt.x + 25, // Close behind DJT
          y: this.djt.y + 5,  // Slightly behind
          duration: 800,
          ease: 'Quad.easeOut',
          onComplete: () => {
            // Switch JD to idle
            if (this.anims.exists('jd-idle')) {
              this.jd.play('jd-idle');
            }
            
            // Kim exits last
            this.kim.setVisible(true);
            this.kim.setPosition(this.limo.x, this.limo.y);
            
            // Play Kim walk animation
            if (this.anims.exists('kim-walk-down')) {
              this.kim.play('kim-walk-down');
            }
            
            this.tweens.add({
              targets: this.kim,
              x: GAME_WIDTH / 2 + 40,
              y: GAME_HEIGHT - 100,
              duration: 800,
              ease: 'Quad.easeOut',
              onComplete: () => {
                // Switch Kim to idle
                if (this.anims.exists('kim-idle')) {
                  this.kim.play('kim-idle');
                }
                // Start dialogue
                this.time.delayedCall(500, () => this.startDialogue());
              }
            });
          }
        });
      }
    });
  }

  private startDialogue(): void {
    const arrivalDialog: DialogConfig = {
      lines: [
        { 
          speaker: 'DJT',
          text: "Kim, you're going to LOVE my friend Clarence Thomas. Tremendous guy, tremendous!"
        },
        {
          speaker: 'DJT',
          text: "You're going to have the BEST time ever here in DC. The best!"
        },
        {
          speaker: 'DJT',
          text: "This is the Temple of Justice. Beautiful, isn't it? I built something even bigger, but this is nice too."
        },
        {
          speaker: 'JD',
          text: "Sir, I just got an urgent call. We need to leave immediately."
        },
        {
          speaker: 'DJT',
          text: "Kim, I'll be back. You explore, have fun. Talk to the Justices - they're all my friends!"
        },
        {
          speaker: 'DJT',
          text: "I'll be back, believe me!"
        },
      ],
      onComplete: () => {
        this.departureSequence();
      }
    };
    
    this.dialogManager.show(arrivalDialog);
  }

  private departureSequence(): void {
    // Play walk animations
    if (this.anims.exists('djt-walk')) {
      this.djt.play('djt-walk');
    }
    if (this.anims.exists('jd-walk')) {
      this.jd.play('jd-walk');
    }
    
    // DJT walks back to limo first
    this.tweens.add({
      targets: this.djt,
      x: this.limo.x,
      y: this.limo.y,
      duration: 1000,
      ease: 'Quad.easeIn',
    });
    
    // JD follows close behind DJT with slight delay
    this.tweens.add({
      targets: this.jd,
      x: this.limo.x + 20,
      y: this.limo.y + 5,
      duration: 1000,
      delay: 150, // Follows behind
      ease: 'Quad.easeIn',
      onComplete: () => {
        this.djt.setVisible(false);
        this.jd.setVisible(false);
        
        // Limo drives away
        this.tweens.add({
          targets: this.limo,
          x: -200,
          duration: 2000,
          ease: 'Quad.easeIn',
          onComplete: () => {
            // Kim approaches temple
            this.kimApproachesTemple();
          }
        });
      }
    });
  }

  private kimApproachesTemple(): void {
    // After limo leaves, enable Kim to walk around freely
    // Position Kim in the grass area
    this.kim.setPosition(GAME_WIDTH / 2, GAME_HEIGHT - 120);
    
    if (this.anims.exists('kim-idle')) {
      this.kim.play('kim-idle');
    }
    
    // Show instructions
    this.dialogManager.say('Kim', "Time to look around! I should head up to the Supreme Court entrance.", () => {
      // Enable walking mode
      this.walkingEnabled = true;
      this.skipText.setText('Arrow keys to move, SPACE near entrance');
    });
  }

  private skipToHub(): void {
    // Clean up and transition to WorldScene
    this.walkingEnabled = false;
    this.dialogManager.destroy();
    this.scene.start('WorldScene');
  }
}
